import { useEffect, useMemo, useState } from "react";
import GameLauncher from "../shared/GameLauncher";
import GameProgress from "../shared/GameProgress";
import GameResults from "../shared/GameResults";
import GameInterpretation from "../shared/GameInterpretation.jsx";
import { getBasicInterpretation } from "../shared/gameInterpretation";
import "./ColorSequence.css";

const COLORS = ["green", "red", "yellow", "blue", "purple", "orange"];

const COLOR_DATA = {
  green: { emoji: "🟢", label: "Verde" },
  red: { emoji: "🔴", label: "Rojo" },
  yellow: { emoji: "🟡", label: "Amarillo" },
  blue: { emoji: "🔵", label: "Azul" },
  purple: { emoji: "🟣", label: "Morado" },
  orange: { emoji: "🟠", label: "Naranja" },
};

const GAME_CONFIG = {
  easy: {
    label: "Fácil",
    colors: COLORS.slice(0, 4),
    startLength: 2,
    maxLength: 5,
    showSpeed: 850,
  },
  medium: {
    label: "Media",
    colors: COLORS.slice(0, 4),
    startLength: 3,
    maxLength: 7,
    showSpeed: 650,
  },
  hard: {
    label: "Difícil",
    colors: COLORS,
    startLength: 4,
    maxLength: 9,
    showSpeed: 500,
  },
};

function randomColor(colors) {
  return colors[Math.floor(Math.random() * colors.length)];
}

function createSequence(length, colors) {
  return Array.from({ length }, () => randomColor(colors));
}

export default function ColorSequence() {
  const [selectedDifficulty, setSelectedDifficulty] = useState(null);
  const [config, setConfig] = useState(null);

  const [sequence, setSequence] = useState([]);
  const [level, setLevel] = useState(0);
  const [userIndex, setUserIndex] = useState(0);

  const [phase, setPhase] = useState("launcher");
  const [activeColor, setActiveColor] = useState(null);

  const [correctLevels, setCorrectLevels] = useState(0);
  const [errors, setErrors] = useState(0);
  const [maxRemembered, setMaxRemembered] = useState(0);

  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);

  const totalLevels = config ? config.maxLength - config.startLength + 1 : 0;

  const progress = useMemo(() => {
    if (!config || totalLevels <= 0) return 0;
    return Math.round((level / totalLevels) * 100);
  }, [level, totalLevels, config]);

  const totalTime =
    startTime && endTime ? Math.round((endTime - startTime) / 1000) : 0;

  const accuracy =
    totalLevels > 0 ? Math.round((correctLevels / totalLevels) * 100) : 0;

  const interpretation =
    phase === "finished"
      ? getBasicInterpretation({
          accuracy,
          errors,
          averageReactionTime: 0,
          difficulty: selectedDifficulty,
        })
      : null;

  const startGame = (difficulty = selectedDifficulty || "easy") => {
    const nextConfig = GAME_CONFIG[difficulty] || GAME_CONFIG.easy;
    const firstSequence = createSequence(
      nextConfig.startLength,
      nextConfig.colors
    );

    setSelectedDifficulty(difficulty);
    setConfig(nextConfig);

    setSequence(firstSequence);
    setLevel(1);
    setUserIndex(0);
    setCorrectLevels(0);
    setErrors(0);
    setMaxRemembered(0);
    setStartTime(Date.now());
    setEndTime(null);
    setActiveColor(null);

    setPhase("showing");
  };

  useEffect(() => {
    if (phase !== "showing" || !sequence.length || !config) return;

    let index = 0;
    let showTimer;
    let hideTimer;

    const showNext = () => {
      if (index >= sequence.length) {
        setActiveColor(null);
        setPhase("playing");
        return;
      }

      setActiveColor(sequence[index]);

      hideTimer = setTimeout(() => {
        setActiveColor(null);
        index += 1;
        showTimer = setTimeout(showNext, 250);
      }, config.showSpeed);
    };

    showTimer = setTimeout(showNext, 500);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [phase, sequence, config]);

  const handleColorPress = (color) => {
    if (phase !== "playing" || !config) return;

    const expectedColor = sequence[userIndex];

    if (color !== expectedColor) {
      setErrors((prev) => prev + 1);
      setEndTime(Date.now());
      setPhase("finished");
      return;
    }

    const nextIndex = userIndex + 1;

    if (nextIndex < sequence.length) {
      setUserIndex(nextIndex);
      return;
    }

    setCorrectLevels((prev) => prev + 1);
    setMaxRemembered(sequence.length);

    if (sequence.length >= config.maxLength) {
      setEndTime(Date.now());
      setPhase("finished");
      return;
    }

    const nextSequence = createSequence(sequence.length + 1, config.colors);

    setSequence(nextSequence);
    setUserIndex(0);
    setLevel((prev) => prev + 1);
    setPhase("showing");
  };

  const backToLauncher = () => {
    setSelectedDifficulty(null);
    setConfig(null);
    setSequence([]);
    setLevel(0);
    setUserIndex(0);
    setPhase("launcher");
    setActiveColor(null);
  };

  if (phase === "launcher") {
    return (
      <GameLauncher
        title="Secuencia de Colores"
        description="Memoriza la secuencia de colores y repítela en el mismo orden."
        areas={["Memoria visual", "Memoria secuencial", "Atención"]}
        defaultDifficulty="easy"
        onStart={startGame}
      />
    );
  }

  return (
    <div className="color-sequence-page">
      <div className="color-sequence-card">
        <header className="color-sequence-header">
          <div>
            <h1>Secuencia de Colores</h1>
            <p>
              Dificultad: <strong>{config?.label}</strong>
            </p>
          </div>

          <button type="button" onClick={() => startGame(selectedDifficulty)}>
            Reiniciar
          </button>
        </header>

        {phase !== "finished" && (
          <>
            <GameProgress
              label="Nivel"
              current={level}
              total={totalLevels}
              progress={progress}
            />

            <div className="sequence-status">
              {phase === "showing" ? "Observa la secuencia..." : "Tu turno"}
            </div>

            <section className="sequence-grid">
              {config.colors.map((color) => (
                <button
                  key={color}
                  type="button"
                  className={`sequence-button ${
                    activeColor === color ? "active" : ""
                  }`}
                  onClick={() => handleColorPress(color)}
                  disabled={phase !== "playing"}
                  aria-label={COLOR_DATA[color].label}
                >
                  {COLOR_DATA[color].emoji}
                </button>
              ))}
            </section>

            <section className="sequence-live-score">
              <span>Nivel: {level}</span>
              <span>Longitud: {sequence.length}</span>
              <span>Errores: {errors}</span>
            </section>
          </>
        )}

        {phase === "finished" && (
          <>
            <GameResults
              results={[
                { label: "Niveles correctos", value: correctLevels },
                { label: "Errores", value: errors },
                { label: "Máx. recordado", value: maxRemembered },
                { label: "Precisión", value: `${accuracy}%` },
                { label: "Tiempo total", value: `${totalTime}s` },
              ]}
              onRepeat={() => startGame(selectedDifficulty)}
              onChangeDifficulty={backToLauncher}
            />

            <GameInterpretation interpretation={interpretation} />
          </>
        )}
      </div>
    </div>
  );
}